import React from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
  TooltipItem,
  Plugin,
  Chart,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { InterpreterInfoTooltip } from './InterpreterInfoTooltip'

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend)

/** Visily App.tsx OPI / IPI metric sections */
const TEXT_MUTED = '#565d6d'
const BORDER_CARD = '#dee1e6'
const BORDER_SOFT = '1px solid rgba(222, 225, 230, 0.6)'
const SHADOW_SM = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
const DIVIDER_LINE = 'rgba(222, 225, 230, 0.6)'
const BADGE_BG = '#fafafb'
const BADGE_BORDER = '1px solid rgba(222, 225, 230, 0.4)'
const FONT_UI = "'Lexend', sans-serif"
const MAX_GAP_SHARE = 0.4

/** Keeps a fixed pixel gap between bars; bar width fills the rest of the category band. */
const lastBarThicknessByChart = new WeakMap<Chart, number>()

function createFixedGapBarThicknessPlugin(gapPx: number, maxThicknessPx?: number): Plugin<'bar'> {
  return {
    id: `interpreterBarGap_${gapPx}`,
    afterLayout(chart) {
      const ds = chart.data.datasets[0] as unknown as
        | {barThickness?: number; maxBarThickness?: number}
        | undefined
      const labels = chart.data.labels
      const n = Array.isArray(labels) ? labels.length : 0
      if (!ds || !chart.chartArea || n < 1) {
        return
      }
      const innerW = chart.chartArea.right - chart.chartArea.left
      const gap = Math.min(gapPx, (innerW / n) * MAX_GAP_SHARE)
      const fitted = Math.max(2, (innerW - gap * Math.max(0, n - 1)) / n)
      const next = maxThicknessPx ? Math.min(fitted, maxThicknessPx) : fitted
      const prev = lastBarThicknessByChart.get(chart)
      if (prev !== undefined && Math.abs(prev - next) < 0.5) {
        return
      }
      lastBarThicknessByChart.set(chart, next)
      ds.barThickness = next
      ds.maxBarThickness = next
      chart.update('none')
    },
  }
}

export type InterpreterMetricCard = {
  id: string
  label: string
  value: string
  subtitle?: string
  /** Corner icon (Visily stat tiles), e.g. `/media/icons/duotune/...` */
  iconSrc?: string
}

export type InterpreterMetricChart = {
  title: string
  tooltipText?: string
  labels: string[]
  values: number[]
  yMax?: number
  yStep?: number
  /** Gap between bars in pixels (bars expand to fill remaining width). Default 5. */
  barGap?: number
  maxBarThickness?: number
  emptyText?: string
  valueLabelSuffix?: string
}

export type InterpreterMetricSection = {
  id: string
  heading: string
  period?: string
  cards: InterpreterMetricCard[]
  chart: InterpreterMetricChart
}

type InterpreterMetricsSectionProps = {
  sections: InterpreterMetricSection[]
}

const barValueLabelPlugin: Plugin<'bar'> = {
  id: 'barValueLabelPlugin',
  afterDatasetsDraw(chart) {
    const meta = chart.getDatasetMeta(0)
    const dataset = chart.data.datasets[0]
    const values = dataset?.data ?? []

    if (!meta || !values.length) {
      return
    }

    const { ctx } = chart
    ctx.save()
    ctx.font = "600 12px 'Lexend', sans-serif"
    ctx.fillStyle = TEXT_MUTED
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'

    meta.data.forEach((bar, index) => {
      const point = bar.tooltipPosition()
      const value = values[index]
      const suffix = (dataset as { valueLabelSuffix?: string }).valueLabelSuffix
      const displayValue = suffix ? `${value} ${suffix}` : String(value)
      ctx.fillText(displayValue, point.x, point.y - 4)
    })

    ctx.restore()
  },
}

export function InterpreterMetricsSection({ sections }: InterpreterMetricsSectionProps) {
  return (
    <>
      {sections.map((section, index) => {
        const maxValue = Math.max(...section.chart.values, 1)
        const yStep = section.chart.yStep ?? 5
        const scaledMax = section.chart.yMax ?? Math.max(yStep * 3, Math.ceil(maxValue / yStep) * yStep)
        const barGap = section.chart.barGap ?? 5
        const gapBarPlugin = createFixedGapBarThicknessPlugin(barGap, section.chart.maxBarThickness)

        const barData = {
          labels: section.chart.labels,
          datasets: [
            {
              label: section.chart.title,
              data: section.chart.values,
              backgroundColor: '#a3a242',
              valueLabelSuffix: section.chart.valueLabelSuffix,
              borderRadius: { topLeft: 2, topRight: 2, bottomLeft: 0, bottomRight: 0 },
              borderSkipped: false,
              categoryPercentage: 1,
              barPercentage: 1,
            },
          ],
        }

        const barOptions = {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 20,
              right: 4,
              left: 2,
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              bodyFont: { family: FONT_UI, size: 12 },
              titleFont: { family: FONT_UI, size: 12 },
              callbacks: {
                label: (ctx: TooltipItem<'bar'>) => `${Number(ctx.parsed.y).toLocaleString()}`,
              },
            },
          },
          scales: {
            x: {
              grid: { display: false, drawBorder: false },
              border: { display: false },
              ticks: {
                color: TEXT_MUTED,
                font: { size: 12, family: FONT_UI },
                padding: 5,
                autoSkip: false,
                maxRotation: 90,
                minRotation: 0,
              },
            },
            y: {
              grid: { color: 'rgba(230, 232, 236, 0.9)', drawBorder: false },
              border: { display: false },
              ticks: {
                color: TEXT_MUTED,
                font: { size: 12, family: FONT_UI },
                precision: 0,
                stepSize: yStep,
                padding: 5,
              },
              beginAtZero: true,
              max: scaledMax,
            },
          },
        }

        const headingRowMarginBottom = section.period ? 8 : 24

        return (
          <section key={section.id} style={{ marginTop: 32 }}>
            <div className='d-flex align-items-center gap-4' style={{ marginBottom: headingRowMarginBottom }}>
              <h4
                className='mb-0 text-nowrap'
                style={{ fontSize: 18, fontWeight: 700, color: '#171a1f' }}
              >
                {section.heading}
              </h4>
              <div className='flex-grow-1' style={{ height: 1, backgroundColor: DIVIDER_LINE }} role='presentation' />
            </div>

            {section.period ? (
              <p className='mb-0' style={{ marginBottom: 16, fontSize: 11, color: TEXT_MUTED }}>
                {section.period}
              </p>
            ) : null}

            <div className='row g-3 mb-4'>
              {section.cards.map((card) => (
                <div key={card.id} className='col-6 col-lg-3 d-flex'>
                  <div
                    className='position-relative w-100 h-100'
                    style={{
                      backgroundColor: '#ffffff',
                      padding: 20,
                      borderRadius: 12,
                      border: BORDER_SOFT,
                      boxShadow: SHADOW_SM,
                    }}
                  >
                    {card.iconSrc ? (
                      <div
                        className='position-absolute d-flex align-items-center justify-content-center'
                        style={{
                          top: 12,
                          right: 12,
                          width: 32,
                          height: 32,
                          backgroundColor: BADGE_BG,
                          border: BADGE_BORDER,
                          borderRadius: 8,
                        }}
                      >
                        <img src={card.iconSrc} alt='' style={{ width: 16, height: 16 }} />
                      </div>
                    ) : null}

                    <p
                      className='text-uppercase mb-0'
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        color: TEXT_MUTED,
                        paddingRight: card.iconSrc ? 36 : 0,
                      }}
                    >
                      {card.label}
                    </p>
                    <h2
                      className='mb-0'
                      style={{
                        marginTop: 32,
                        fontFamily: FONT_UI,
                        fontSize: 36,
                        fontWeight: 400,
                        lineHeight: 1.1,
                        color: '#171a1f',
                      }}
                    >
                      {card.value}
                    </h2>
                    {card.subtitle ? (
                      <p className='mb-0' style={{ marginTop: 8, fontSize: 11, color: TEXT_MUTED }}>
                        {card.subtitle}
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: 24,
                backgroundColor: '#ffffff',
                padding: 24,
                borderRadius: 12,
                border: `1px solid ${BORDER_CARD}`,
                boxShadow: SHADOW_SM,
              }}
            >
              <div className='d-flex align-items-center gap-2' style={{ marginBottom: 24 }}>
                <p
                  className='text-uppercase mb-0'
                  style={{
                    fontSize: 14,
                    fontWeight: 900,
                    letterSpacing: '0.05em',
                    color: TEXT_MUTED,
                  }}
                >
                  {section.chart.title}
                </p>
                {section.chart.tooltipText ? (
                  <InterpreterInfoTooltip id={`${section.id}-growth-tooltip`} text={section.chart.tooltipText} />
                ) : null}
              </div>
              <div style={{ width: '100%', aspectRatio: '3 / 1', position: 'relative', minHeight: 160 }}>
                <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}>
                  {section.chart.emptyText && section.chart.values.length === 0 ? (
                    <div
                      className='d-flex align-items-center justify-content-center h-100'
                      style={{ fontSize: 12, color: TEXT_MUTED }}
                    >
                      {section.chart.emptyText}
                    </div>
                  ) : (
                    <Bar data={barData} options={barOptions} plugins={[gapBarPlugin, barValueLabelPlugin]} />
                  )}
                </div>
              </div>
            </div>
          </section>
        )
      })}
    </>
  )
}
